import React from 'react';
import { Layout, Typography, Row, Col, Card, Space, Button, Table, Tag, Grid } from 'antd';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { LinkOutlined, MailOutlined, GiftOutlined, UsergroupAddOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { getCurrentUser } from '../utils/userStorage';
import { useCurrentUser } from '../hooks/useCurrentUser';
import BackLink from '../components/BackLink';
import fetchReferralsList from '../utils/referral/fetchReferralsList';
import { logEvent } from '../analytics';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const buildReferralLink = (referralCode) => {
  return referralCode ? `https://itaca.ai/ref/${encodeURIComponent(referralCode)}` : 'https://itaca.ai';
};

const Referrals = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const currentUser = useCurrentUser(['referral_code']);

  const referralCode = currentUser?.referral_code || '';
  const shareUrl = React.useMemo(() => buildReferralLink(referralCode), [referralCode]);

  const [copyLoading, setCopyLoading] = React.useState(false);
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [whatsAppLoading, setWhatsAppLoading] = React.useState(false);

  const [tableLoading, setTableLoading] = React.useState(true);
  const [referrals, setReferrals] = React.useState([]);
  const [pagination, setPagination] = React.useState({ current: 1, pageSize: 10, total: 0 });

  const handleCopy = async () => {
    setCopyLoading(true);
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('referrals.link_copied'));
      logEvent('Referrals', 'CopyLink');
    } catch (e) {
      toast.error(t('referrals.copy_failed'));
    } finally {
      setCopyLoading(false);
    }
  };

  const handleSendEmail = () => {
    setEmailLoading(true);
    try {
      const subject = encodeURIComponent(t('referrals.email_subject'));
      const body = encodeURIComponent(`${t('referrals.share_link_body')}${shareUrl}`);
      const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
      window.open(mailtoLink, '_blank');
      logEvent('Referrals', 'SendEmail');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendWhatsApp = () => {
    setWhatsAppLoading(true);
    try {
      const body = encodeURIComponent(`${t('referrals.share_link_body')}: ${shareUrl}`);
      const waLink = `https://wa.me/?text=${body}`;
      window.open(waLink, '_blank');
      logEvent('Referrals', 'SendWhatsApp');
    } finally {
      setWhatsAppLoading(false);
    }
  };

  const loadReferrals = async (page = 1, pageSize = 10) => {
    setTableLoading(true);
    try {
      const result = await fetchReferralsList(page, { per_page: pageSize });
      setReferrals(result.referrals);
      setPagination(result.pagination || { current: page, pageSize, total: result.referrals.length });
    } catch (error) {
      // Graceful fallback if API not available yet
      setReferrals([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setTableLoading(false);
    }
  };

  React.useEffect(() => {
    loadReferrals(1, pagination.pageSize);
  }, []);

  const columns = [
    {
      title: t('referrals.table.name'),
      dataIndex: 'referred',
      key: 'referred',
      render: (_text, record) => {
        return record.getDisplayName();
      }
    },
    {
      title: t('referrals.table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (_status, record) => {
        const color = record.getReferralStatus() === 'upgraded' ? 'green' : 'default';
        const label = record.getDisplayStatus();
        return <Tag color={color}>{label}</Tag>;
      }
    }
  ];

  const handleTableChange = (pager) => {
    const { current, pageSize } = pager;
    setPagination((prev) => ({ ...prev, current, pageSize }));
    loadReferrals(current, pageSize);
  };

  const upgradedCount = referrals.filter(r => r.getReferralStatus() === 'upgraded').length;

  return (
    <Content className="page referrals-page" data-testid="referrals-page">
      {isMobile && (
        <Row align="middle" gutter={[16, 16]} style={{ marginBottom: '8px', marginLeft: '0px' }}>
          <Col flex="none" style={{ textAlign: 'left', paddingLeft: 0 }}>
            <BackLink to="/" label={t('common.back')} />
          </Col>
        </Row>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>
        <Card
          data-testid="referrals-hero"
          style={{
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
            color: 'white',
            border: 'none'
          }}
        >
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Title level={2} style={{ color: 'white', margin: 0 }}>
              {t('referrals.title')}
            </Title>
            {!isMobile && <Paragraph style={{ fontSize: '17px', color: 'rgba(255,255,255,0.9)', marginTop: 8, marginBottom: 0 }}>
              {t('referrals.subtitle')}
            </Paragraph>}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Tag color="gold" style={{ borderRadius: 15, padding: '6px 12px', fontSize: 14 }}>
                <GiftOutlined /> <span style={{ marginLeft: 8 }}>{t('referrals.perk.you')}</span>
              </Tag>
              <Tag color="blue" style={{ borderRadius: 15, padding: '6px 12px', fontSize: 14 }}>
                <UsergroupAddOutlined /> <span style={{ marginLeft: 8 }}>{t('referrals.perk.them')}</span>
              </Tag>
            </div>
          </div>
        </Card>

        <Card style={{ marginBottom: '24px' }} data-testid="referrals-share">
          <Text style={{ fontSize: '15px', display: 'block', marginBottom: 8, color: 'rgba(0,0,0,0.6)' }}>
            {t('referrals.share_your_link')}
          </Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            <div
              data-testid="referrals-share-link"
              title={shareUrl}
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '8px 12px',
                height: 40,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                width: '100%',
                maxWidth: 420
              }}
            >
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
                fontSize: 15,
                color: '#555'
              }}>
                {shareUrl}
              </span>
            </div>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} align={isMobile ? 'start' : 'center'} size="middle">
              <Button type="primary" icon={<LinkOutlined />} size="large" onClick={handleCopy} loading={copyLoading}>
                {t('referrals.copy_link')}
              </Button>
              <Button icon={<WhatsAppOutlined />} size="large" onClick={handleSendWhatsApp} loading={whatsAppLoading}>
                {t('referrals.send_whatsapp')}
              </Button>
              <Button icon={<MailOutlined />} size="large" onClick={handleSendEmail} loading={emailLoading}>
                {t('referrals.send_email')}
              </Button>
            </Space>
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>{t('referrals.activity')}</Title>
          </div>
          <Table
            data-testid="referrals-table"
            rowKey={(r) => r.getEmail()}
            columns={columns}
            dataSource={referrals}
            loading={tableLoading}
            pagination={pagination}
            onChange={handleTableChange}
          />
        </Card>

        <div data-testid="referrals-footer" style={{ marginTop: 25 }}>
          <Text type="secondary" style={{ fontSize: 14 }}>
            * {t('referrals.footer.note')}
          </Text>
        </div>
      </div>
    </Content>
  );
};

export default Referrals;



